import { OptionInputProps, RenderOption } from '@ui-components-3/ui/lib/components/RadioGroup/types'
import { classNames } from '@ui-components-3/ui'
import { useSelector } from 'react-redux'
import { getServiceByServiceType } from 'redux/selectors/services'
import { ReactNode, useMemo } from 'react'
import { useOperatingHoursRanges } from 'hooks/operating-hours/useOperatingHoursRanges'
import OperatingHoursInfo from 'components/visit-creation/OperatingHoursInfo'
import { Service } from 'types/member/member'

const ROUNDED_CLASS = 'rounded-[12px]'

type OptionContentProps = {
  id: string
  optionDisabled?: boolean
  label: ReactNode
  inputProps: OptionInputProps
  labelProps: Record<string, any>
  shouldCheckAfterHours: boolean
}

const OptionContent = ({
  id,
  optionDisabled,
  label,
  inputProps,
  labelProps,
  shouldCheckAfterHours,
}: OptionContentProps) => {
  const { prefix, suffix, ...otherLabelProps } = labelProps || {}

  const { value: serviceType } = inputProps || {}

  const selectedService: Service = useSelector(getServiceByServiceType(serviceType))

  const initialOperatingHoursDate = useMemo(() => new Date(), [])

  const { isInOperatingHoursRange, operatingHoursRanges } = useOperatingHoursRanges({
    initialDate: initialOperatingHoursDate,
    serviceType: selectedService?.serviceType,
    shouldSkip: !shouldCheckAfterHours,
  })

  const disabled = optionDisabled || isInOperatingHoursRange

  return (
    <div className="flex flex-col items-start gap-y-1">
      {!!isInOperatingHoursRange && (
        <OperatingHoursInfo initialDate={initialOperatingHoursDate} operatingHoursRanges={operatingHoursRanges} />
      )}
      <label
        {...otherLabelProps}
        htmlFor={id}
        className={classNames(
          'group relative flex h-full w-full overflow-hidden bg-white [&>div>input[type="radio"]]:self-center [&>div]:w-full [&>div]:items-stretch',
          ROUNDED_CLASS,
          disabled ? 'bg-neutral-200 opacity-50 [&>div]:opacity-70' : 'cursor-pointer hover:bg-neutral-100',
        )}
      >
        {prefix}
        <div className={classNames('p-s gap-s flex items-center justify-between text-neutral-600')}>
          {label}
          <input
            {...inputProps}
            disabled={disabled}
            id={id}
            className={classNames('peer flex-none', inputProps.className)}
          />
          <div
            aria-hidden="true"
            className={classNames(
              'pointer-events-none absolute inset-0 z-10 border-2 border-neutral-300',
              disabled ? '' : 'peer-checked:border-primary-600 group-hover:border-primary-500',
              ROUNDED_CLASS,
            )}
          />
        </div>
        {suffix}
      </label>
    </div>
  )
}

const RenderVisitTypeCard: (shouldCheckAfterHours: boolean) => RenderOption =
  (shouldCheckAfterHours) => (id, optionDisabled, label, inputProps, labelProps) => {
    return (
      <OptionContent
        shouldCheckAfterHours={shouldCheckAfterHours}
        id={id}
        optionDisabled={optionDisabled}
        label={label}
        inputProps={inputProps}
        labelProps={labelProps}
      />
    )
  }

export default RenderVisitTypeCard
